import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu } from "lucide-react";

export const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);

  const navLinks = [
    { label: "Why Hire Us", href: "/why-hire-us" },
    { label: "Our Process", href: "/management-process" },
    { label: "Marketing", href: "/marketing-process" },
    { label: "Areas", href: "/#areas" },
    { label: "FAQ", href: "/owners-faq" },
    { label: "Contact", href: "/#contact" },
  ];

  return (
    <header className="sticky top-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/90 border-b border-border/60 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20 lg:h-24">
          {/* Logo */}
          <a href="/" className="flex items-center flex-shrink-0">
            <img 
              src="/rently-logo.png" 
              alt="Rently Properties Logo" 
              className="h-14 w-auto md:h-16 lg:h-18"
            />
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1 xl:space-x-2">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                data-testid="header-nav-link"
                className="px-3 xl:px-4 py-2 text-base font-medium text-foreground/80 hover:text-primary hover:bg-primary/5 rounded-lg transition-all"
              >
                {link.label}
              </a>
            ))}
          </nav>

          {/* Desktop Phone & CTA */}
          <div className="hidden lg:flex items-center space-x-4 xl:space-x-6">
            <a 
              href="tel:913-795-1155" 
              className="text-lg xl:text-xl font-bold text-primary hover:text-primary/80 transition-colors flex items-center gap-2 whitespace-nowrap"
            >
              <span className="text-2xl">📞</span>
              <span className="hidden xl:inline">913.795.1155</span>
              <span className="xl:hidden">Call Now</span>
            </a>
            <Button 
              data-testid="header-primary-cta-button" 
              size="lg" 
              className="bg-secondary hover:bg-secondary/90 text-base font-semibold px-5 xl:px-6 whitespace-nowrap"
              asChild
            >
              <a href="/get-free-analysis">Get Free Analysis</a>
            </Button>
          </div>

          {/* Mobile Phone & Menu */}
          <div className="flex items-center gap-3 lg:hidden">
            <a 
              href="tel:913-795-1155" 
              className="text-xl font-bold text-primary flex items-center"
            >
              <span className="text-2xl">📞</span>
            </a>
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  data-testid="header-mobile-menu-button"
                  className="h-10 w-10"
                >
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] sm:w-[350px]">
                <nav className="flex flex-col space-y-1 mt-8">
                  <a 
                    href="tel:913-795-1155" 
                    className="text-xl font-bold text-primary hover:text-primary/80 transition-colors flex items-center gap-3 pb-6 mb-4 border-b"
                  >
                    <span className="text-2xl">📞</span>
                    <span>913.795.1155</span>
                  </a>
                  {navLinks.map((link) => (
                    <a
                      key={link.label}
                      href={link.href}
                      data-testid="header-nav-link"
                      className="text-lg font-medium text-foreground/80 hover:text-primary hover:bg-primary/5 px-4 py-3 rounded-lg transition-all"
                      onClick={() => setMobileOpen(false)}
                    >
                      {link.label}
                    </a>
                  ))}
                  <Button 
                    data-testid="header-primary-cta-button" 
                    size="lg" 
                    className="mt-6 bg-secondary hover:bg-secondary/90 w-full"
                    asChild
                    onClick={() => setMobileOpen(false)}
                  >
                    <a href="/get-free-analysis">Get Free Analysis</a>
                  </Button>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
